<template>
    <!-- Only display the module if there are upcoming events or it's still loading -->
    <section v-if="isLoading || upcomingEvents.length" class="upcoming-section py-16 px-8">
        <div class="max-w-full mx-auto">
            <div class="text-left mb-8">
                <h2 class="text-3xl font-bold text-gray-800">{{ $translate('Upcoming Events') }}</h2>
                <p class="text-lg text-gray-600 mt-2">
                    {{ $translate('Do you want to participate in one of the other upcoming events?') }}
                </p>
            </div>

            <!-- Loading Display -->
            <div v-if="isLoading" class="text-center">
                <p>{{ $translate('Loading upcoming events...') }}</p>
            </div>

            <!-- Slider Container -->
            <div v-else class="relative">
                <!-- Left Arrow -->
                <div v-if="canScrollLeft" class="arrow-container left-arrow">
                    <font-awesome-icon :icon="['fas', 'chevron-left']" class="arrow-icon" @click="scrollLeft" />
                </div>

                <!-- Horizontal Slideshow with calculated widths and gap -->
                <div ref="slider" class="flex gap-4 overflow-x-auto no-scrollbar scroll-smooth snap-x snap-mandatory"
                    @scroll="updateArrowVisibility">
                    <EventCard v-for="(event, index) in upcomingEvents" :key="index" :event="event" class="card" />
                </div>

                <!-- Right Arrow -->
                <div v-if="canScrollRight" class="arrow-container right-arrow">
                    <font-awesome-icon :icon="['fas', 'chevron-right']" class="arrow-icon" @click="scrollRight" />
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import EventCard from '@/components/default/card/EventCard.vue';
import { cookie } from '@/utils/cookie';

export default {
    name: 'UpcomingModule',
    components: {
        EventCard,
    },
    props: {
        isSignedUp: {
            type: Boolean,
            required: true,
        },
        isPayable: {
            type: Boolean,
            required: true,
        },
        event: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            isLoading: true,
            upcomingEvents: [],
            canScrollLeft: false,
            canScrollRight: true,
        };
    },
    computed: {
        excludeIds() {
            return [this.event.id]; // Exclude the current event from the upcoming list
        }
    },
    methods: {
        async fetchUpcomingEvents() {
            try {
                const response = await fetch(`${window.vueAppConfig.endpoint}/api/v2/events/list`, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        status: 1,
                        brand_id: window.vueAppConfig.info.id,
                        session_id: cookie(),
                        exclude_ids: JSON.stringify(this.excludeIds), // Pass excludeIds directly
                    }),
                });
                const { status, data } = await response.json();

                if (status && Array.isArray(data)) {
                    this.upcomingEvents = data;
                } else {
                    console.error('Invalid response data format:', data);
                    this.upcomingEvents = [];
                }
            } catch (error) {
                console.error('Error fetching upcoming events:', error);
                this.upcomingEvents = [];
            } finally {
                this.isLoading = false;
                this.$nextTick(() => {
                    this.updateArrowVisibility();
                });
            }
        },
        scrollLeft() {
            const slider = this.$refs.slider;
            if (slider) {
                const cardWidth = slider.querySelector('.card').offsetWidth + 16; // card width plus gap
                slider.scrollBy({ left: -cardWidth, behavior: 'smooth' });
            }
        },
        scrollRight() {
            const slider = this.$refs.slider;
            if (slider) {
                const cardWidth = slider.querySelector('.card').offsetWidth + 16; // card width plus gap
                slider.scrollBy({ left: cardWidth, behavior: 'smooth' });
            }
        },
        updateArrowVisibility() {
            const slider = this.$refs.slider;
            if (slider) {
                this.canScrollLeft = slider.scrollLeft > 0;
                this.canScrollRight =
                    slider.scrollLeft + slider.clientWidth < slider.scrollWidth - 1;
            }
        },
    },
    mounted() {
        this.fetchUpcomingEvents();
        // Update arrow visibility on scroll
        this.$refs.slider?.addEventListener('scroll', this.updateArrowVisibility);
    },
    beforeUnmount() {
        this.$refs.slider?.removeEventListener('scroll', this.updateArrowVisibility);
    },
};
</script>

<style scoped>
.upcoming-section {
    margin: 0 auto;
    border-top: 1px solid #e2e8f0;
}

.no-scrollbar {
    scrollbar-width: none;
    /* Firefox */
}

.no-scrollbar::-webkit-scrollbar {
    display: none;
    /* Safari and Chrome */
}

/* Smooth scrolling */
.scroll-smooth {
    scroll-behavior: smooth;
}

/* Scroll snapping */
.snap-x {
    scroll-snap-type: x mandatory;
}

.card {
    flex-shrink: 0;
    width: 100%;
    scroll-snap-align: start;
}

@media (min-width: 640px) {

    /* sm */
    .card {
        width: calc((100% - 16px) / 2);
        /* 1 gap of 16px */
    }
}

@media (min-width: 768px) {

    /* md */
    .card {
        width: calc((100% - 32px) / 3);
        /* 2 gaps of 16px */
    }
}

@media (min-width: 1600px) {

    /* lg */
    .card {
        width: calc((100% - 48px) / 4);
        /* 3 gaps of 16px */
    }
}

/* Arrow styles */
.arrow-container {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
    background-color: rgba(0, 0, 0, 0.6);
    border-radius: 50%;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    cursor: pointer;
    transition: background-color 0.3s, box-shadow 0.3s;
}

.arrow-container:hover {
    background-color: rgba(0, 0, 0, 0.8);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
}

.left-arrow {
    left: 10px;
}

.right-arrow {
    right: 10px;
}

.arrow-icon {
    color: #fff;
    font-size: 24px;
}
</style>
